import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import detect from 'detect.js'
import { useQueryParams } from '@calendly/hooks'

import { currentUserSelector } from 'selectors/user'
import { isEmbeddedSelector } from 'selectors/embed'

import { useTrackCommerce } from './useTrackCommerce'

/**
 * Fires a single 'Booking Page Viewed' commerce event when the invoice page loads.
 *
 * utm logic:
 *   - If share_source is present in the URL: utm_source and utm_content are both null.
 *   - Otherwise: utm_source is 'invoice', utm_content mirrors the URL query param (or null).
 *
 * Retry behavior: if track() returns false (e.g., cookies not yet accepted), the
 * hook does not lock in hasTrackedRef, so a subsequent render -- when the user
 * accepts OneTrust consent or any tracked dep changes -- will re-attempt the
 * dispatch.
 *
 * @param {Object|null} invoice - Invoice from useInvoicePageData(); needs `uuid`
 *   and (for analytics joins) `owner_uuid` (host user uuid).
 */
export const useTrackInvoicePageView = (invoice) => {
  const track = useTrackCommerce()
  const currentUser = useSelector(currentUserSelector)
  const isEmbedded = useSelector(isEmbeddedSelector)
  const { utm_content: utmContent, share_source: shareSource } = useQueryParams()
  const hasTrackedRef = useRef(false)
  const isTrackingRef = useRef(false)

  useEffect(() => {
    if (!invoice?.uuid || hasTrackedRef.current || isTrackingRef.current) return

    const ua = detect.parse(navigator.userAgent)
    const hasShareSource = typeof shareSource === 'string' && shareSource.length > 0
    const utmContentFromUrl = typeof utmContent === 'string' ? utmContent : null

    const properties = {
      booking_page_type: 'invoice_checkout',
      invitee_device: `${ua.device.type} ${ua.os.name}`,
      is_calendly_user: !!currentUser?.uuid,
      is_embedded: isEmbedded,
      is_owner_session: !!(invoice.owner_uuid && currentUser?.uuid)
        && invoice.owner_uuid === currentUser.uuid,
      owner_type: 'invoice',
      // owner_id is integer|null in the schema; invoice identity is sent as invoice_uuid.
      owner_id: null,
      invoice_uuid: invoice.uuid,
      utm_source: hasShareSource ? null : 'invoice',
      utm_content: hasShareSource ? null : utmContentFromUrl,
    }

    const dependencies = invoice.owner_uuid ? { user_uuid: invoice.owner_uuid } : {}

    const run = async () => {
      isTrackingRef.current = true
      try {
        const dispatched = await track('Booking Page Viewed', properties, dependencies)
        if (dispatched) hasTrackedRef.current = true
      } catch (e) {
        console.error(`Dispatching Booking Page Viewed failed.\n\n${e?.message || e}`)
      } finally {
        isTrackingRef.current = false
      }
    }

    run()
  }, [invoice, utmContent, shareSource, track, currentUser?.uuid, isEmbedded])
}
